import React, { useState, useRef, useCallback } from 'react';
import { PhaseControlProps } from '../types';

const PhaseControl: React.FC<PhaseControlProps> = ({ phase, setPhase, isDisabled }) => {
  const knobRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const updateFromPoint = useCallback((clientX: number, clientY: number) => {
    if (isDisabled || !knobRef.current) return;
    const rect = knobRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const angle = Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
    setPhase((angle + 90 + 360) % 360); // Offset di 90 gradi per avere lo 0 in alto
  }, [setPhase, isDisabled]);

  // Pointer events: gestiscono mouse, touch e penna con un solo codice
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    updateFromPoint(e.clientX, e.clientY);
  }, [isDisabled, updateFromPoint]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isDragging) updateFromPoint(e.clientX, e.clientY);
  }, [isDragging, updateFromPoint]);

  const handlePointerUp = useCallback(() => setIsDragging(false), []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    const step = e.shiftKey ? 15 : 5;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') setPhase((phase + step) % 360);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') setPhase((phase - step + 360) % 360);
    else return;
    e.preventDefault();
  }, [isDisabled, phase, setPhase]);

  return (
    <div className={`flex flex-col items-center space-y-2 p-4 bg-slate-800/60 backdrop-blur-sm rounded-lg border border-slate-700 ${isDisabled ? 'opacity-50' : ''}`}>
      <div className="flex justify-between items-baseline w-full px-2">
         <span className={`text-sm font-medium transition-colors ${isDisabled ? 'text-slate-500' : 'text-slate-300'}`}>Fase Motore</span>
         <span className={`text-sm font-bold ${isDisabled ? 'text-slate-500' : 'text-slate-300'}`}>Manuale</span>
      </div><br/>
      <div
        ref={knobRef}
        className={`relative w-40 h-40 rounded-full bg-slate-900 border-4 border-slate-700 flex items-center justify-center select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${isDisabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
        style={{ touchAction: 'none' }}
        role="slider"
        aria-label="Fase motore"
        aria-valuemin={0}
        aria-valuemax={359}
        aria-valuenow={Math.round(phase)}
        aria-valuetext={`${Math.round(phase)} gradi`}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onKeyDown={handleKeyDown}
      >
        {/* Markings */}
        <span className="absolute -top-1 text-xs text-slate-400 transform -translate-y-full">0°</span>
        <span className="absolute -bottom-1 text-xs text-slate-400 transform translate-y-full">180°</span>
        <span className="absolute -left-1 text-xs text-slate-400 transform -translate-x-full pr-1">270°</span>
        <span className="absolute -right-1 text-xs text-slate-400 transform translate-x-full pl-1">90°</span>

        {/* Inner dial */}
        <div className="w-32 h-32 rounded-full bg-slate-800 flex items-center justify-center shadow-inner">
            <span className="text-3xl font-bold text-cyan-400 tracking-tighter">{Math.round(phase)}°</span>
        </div>

        {/* Knob indicator */}
        <div
          className="absolute top-0 left-1/2 w-1 h-1/2 origin-bottom"
          style={{ transform: `rotate(${phase}deg)`}}
        >
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-cyan-400 rounded-full shadow-[0_0_8px_2px] shadow-cyan-400/50" />
        </div>
      </div>
    </div>
  );
};

export default PhaseControl;
